import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { BroadcastComposeStoreApi } from "@/features/BroadcastV2/store/broadcastComposeStore";
import type { TextComposerAuthoringState } from "@/features/TextComposerV2/editor/authoringState";
import type { TextComposerExtension } from "@/features/TextComposerV2/editor/extensionContract";

import {
  assertNoOwnedKeyCollisions,
  resolveExtensionOrder,
} from "@/features/TextComposerV2/editor/extensionRegistry";
import {
  createMessageDraftStore,
  MessageDraftStoreContext,
  type MessageDraftContextValue,
  type MessageDraftStoreApi,
} from "@/features/TextComposerV2/editor/messageDraftStoreFactory";
import { defaultExtensions } from "@/features/TextComposerV2/extensions/defaultExtensions";

export interface VariationDraftStoresApi {
  /** Lazily create (if absent) and return the editor store for a variation id. */
  getStore: (id: string) => MessageDraftStoreApi;
  /**
   * Stable, memoized context value for a variation id. Safe to pass straight
   * to `MessageDraftStoreContext.Provider` — the reference is preserved across
   * tab switches so Lexical editors are never remounted (and never lose typing
   * or cursor position).
   */
  getContext: (id: string) => MessageDraftContextValue;
  /** Deep-copy one variation's authoring state into a new variation's store. */
  copyStore: (fromId: string, toId: string) => void;
  extensions: TextComposerExtension[];
  MessageDraftStoreContext: typeof MessageDraftStoreContext;
}

export interface UseVariationDraftStoresArgs {
  /** The compose store — the source of truth for variation ids/order. */
  storeApi: BroadcastComposeStoreApi;
  /** Edit-mode initial authoring seeds, keyed by variation id. */
  seedsById?: Record<string, TextComposerAuthoringState>;
  /**
   * Initial channel-preview toggle for every variation's editor. Defaults to
   * "primary"; pass "fallback" to open on the SMS/MMS side (e.g. when the site
   * only supports SMS).
   */
  initialPreviewRecipientViewMode?: "primary" | "fallback";
}

/**
 * Owns one `MessageDraftStore` (Lexical editor state) per SMS variation,
 * keyed by the variation's stable `id`. This is the SMS analogue of the inline
 * `email` payload that email variations carry — but a draft store is a live,
 * non-serializable object, so it lives here rather than inside the Immer-managed
 * compose store.
 *
 * The compose store (`storeApi`) remains the single source of truth for variation
 * identity, order and the active index; this hook reconciles against it: stores
 * are created lazily on first access and disposed (by id set-difference) when a
 * variation is removed. Identity-keying — not position — is what makes removing a
 * non-last variation safe.
 */
export function useVariationDraftStores({
  storeApi,
  seedsById,
  initialPreviewRecipientViewMode,
}: UseVariationDraftStoresArgs): VariationDraftStoresApi {
  // One frozen, validated extension array shared by every store, so each
  // context's `extensions` reference is stable and per-store schemas match.
  const extensionsRef = useRef<TextComposerExtension[]>(undefined);
  if (!extensionsRef.current) {
    assertNoOwnedKeyCollisions(defaultExtensions);
    extensionsRef.current = resolveExtensionOrder(defaultExtensions);
  }
  const extensions = extensionsRef.current;

  // Latest seeds, read lazily by getStore without re-creating callbacks.
  const seedsRef = useRef(seedsById);
  seedsRef.current = seedsById;

  const registryRef = useRef<Map<string, MessageDraftStoreApi>>(undefined);
  if (!registryRef.current) registryRef.current = new Map();
  const ctxCacheRef = useRef<Map<string, MessageDraftContextValue>>(undefined);
  if (!ctxCacheRef.current) ctxCacheRef.current = new Map();

  const [, forceRender] = useState(0);

  const getStore = useCallback(
    (id: string): MessageDraftStoreApi => {
      const registry = registryRef.current!;
      let store = registry.get(id);
      if (!store) {
        store = createMessageDraftStore(seedsRef.current?.[id] ?? {}, {
          extensions,
          initialPreviewRecipientViewMode,
        });
        registry.set(id, store);
      }
      return store;
    },
    [extensions, initialPreviewRecipientViewMode],
  );

  const getContext = useCallback(
    (id: string): MessageDraftContextValue => {
      const cache = ctxCacheRef.current!;
      let ctx = cache.get(id);
      if (!ctx) {
        ctx = { store: getStore(id), extensions };
        cache.set(id, ctx);
      }
      return ctx;
    },
    [extensions, getStore],
  );

  const copyStore = useCallback(
    (fromId: string, toId: string) => {
      const source = getStore(fromId).getState().authoringState;
      // createMessageDraftStore runs normalizeAuthoringDraft → schema.parse,
      // which returns fresh objects, so this is an effective deep copy.
      const copy = createMessageDraftStore(
        { ...source },
        { extensions, initialPreviewRecipientViewMode },
      );
      registryRef.current!.set(toId, copy);
      ctxCacheRef.current!.delete(toId); // ensure a fresh ctx wraps the copy
    },
    [extensions, getStore, initialPreviewRecipientViewMode],
  );

  // Dispose-only reconcile: drop stores whose variation id is no longer present.
  // Never creates here (creation stays lazy in getStore) to avoid feedback loops.
  useEffect(() => {
    function reconcile(ids: string[]) {
      const idSet = new Set(ids);
      const registry = registryRef.current!;
      const ctxCache = ctxCacheRef.current!;
      let disposed = false;
      for (const key of [...registry.keys()]) {
        if (!idSet.has(key)) {
          registry.delete(key);
          ctxCache.delete(key);
          disposed = true;
        }
      }
      if (disposed) forceRender((n) => n + 1);
    }

    // Keep stashed (A/B toggled-off) variations' stores alive too, so their
    // editor content survives a toggle round-trip.
    const keepIds = (state: ReturnType<typeof storeApi.getState>) =>
      [...state.variations, ...state.stashedVariations].map((v) => v.id);
    reconcile(keepIds(storeApi.getState()));
    return storeApi.subscribe((state) => reconcile(keepIds(state)));
  }, [storeApi]);

  return useMemo(
    () => ({
      getStore,
      getContext,
      copyStore,
      extensions,
      MessageDraftStoreContext,
    }),
    [getStore, getContext, copyStore, extensions],
  );
}

/**
 * Add an SMS variation and synchronously copy the active variation's editor
 * content into the new variation's store. The copy must happen in the same
 * handler — after `addVariation` commits the new id, before the new editor
 * first renders — or the lazily-created store would render empty.
 */
export function useAddSmsVariation(
  storeApi: BroadcastComposeStoreApi,
  registry: VariationDraftStoresApi,
): () => void {
  return useCallback(() => {
    const state = storeApi.getState();
    const fromIndex = state.activeVariationIndex;
    const sourceId = state.variations[fromIndex]?.id;
    const before = state.variations.length;

    state.actions.addVariation({ copyFromIndex: fromIndex });

    const next = storeApi.getState();
    if (next.variations.length > before && sourceId) {
      const newId = next.variations[next.variations.length - 1].id;
      registry.copyStore(sourceId, newId);
    }
  }, [storeApi, registry]);
}
